/**
 * Note Component
 *
 * @component
 * @description This component represents a note and provides an interface to view, edit, and delete it.
 * It displays the note's content, author, creation date, and provides actions to edit or delete the note.
 *
 * @param {NoteItemProps} props - The properties and event handlers required by the component.
 * @returns {JSX.Element} - A JSX element representing the Note component.
 */

import React, { useCallback, useState } from 'react';
import { Grid } from '@mui/system';
import { CALIBRATION_PAGE_VALUE } from 'common/constants/notes';
import useNotesContext from 'context/NotesContext';
import { NoteData, NoteItemProps } from '../types';
import NoteEdit from './NoteEdit';

const Note = ({ note, isDisabled, autoFocus }: NoteItemProps) => {
  const { onUpdateNotes } = useNotesContext();
  const { content } = note;
  const [noteContent, setNoteContent] = useState<string>(content);
  const isCalibrationNote = note.page_type === CALIBRATION_PAGE_VALUE;

  /**
   * @function
   * @name handleOnUpdateContentNote
   * @description Updates local note content and immediately syncs the parent note state.
   * @param {string} html - The HTML content from the rich text editor.
   * @return {void}
   */
  const handleOnUpdateContentNote = useCallback((html: string) => {
    setNoteContent(html);

    const noteData: NoteData = {
      ...note,
      isNew: false,
      content: html,
    };
    onUpdateNotes?.(noteData);
  }, [note, onUpdateNotes]);

  return (
    <Grid container size={{ xs: 12 }}>
      <NoteEdit
        noteContent={noteContent}
        isDisabled={isDisabled}
        handleOnChangeContentNote={handleOnUpdateContentNote}
        autoFocus={autoFocus}
        hasExtraTopSpacing={isCalibrationNote}
      />
    </Grid>
  );
};

export default Note;
